<template>

    <LoadingPage v-if="disableSendingButton"></LoadingPage>

    <div class="row text-center justify-content-center">
        <h2 class="modal-title"><strong>Send Proposal</strong></h2>
    </div>

    <div class="row mt-2 mb-2 text-center">
        <h6 class="card-subtitle text-muted">Date: <strong>{{ this.currentDate }}</strong></h6>
    </div>

    <form ref="proposalFormRef" class="row g-0 mx-4" style="gap: 0.5rem">
        <div class="mb-3">
            <label for="proposalTitle" class="form-label">Proposal Title<span class="text-danger">*</span> :</label>
            <input type="text" id="proposalTitle" class="form-control" v-model="proposalTitle" placeholder="Enter proposal title" required>
        </div>

        <!-- ABN & Organisation Name Force-Check ONLY if AU lead has no ABN -->
        <div v-if="isAustralianLead && !initialLeadHasAbn && !isLoadingProposalDetails" class="border rounded p-3 mb-3 bg-light text-start" style="border-color: #ffeeba !important;">
            <div class="d-flex align-items-center mb-2 text-warning">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <strong>{{ businessNumberShortLabel }} Required</strong>
            </div>
            <p class="small text-muted mb-3" style="font-size: 13px;">{{ businessNumberShortLabel }} is required for this organisation. Please verify and provide the {{ businessNumberShortLabel }} and Organisation Name before sending the proposal.</p>
            
            <div class="mb-3 text-start">
                <label for="leadAbn" class="form-label" style="font-size: 13px;">{{ businessNumberLabel }} <span class="text-danger">*</span> :</label>
                <input type="text" id="leadAbn" v-model.trim="leadAbn" @keyup="handleAbnInput" class="form-control"
                    :placeholder="businessNumberPlaceholder" :disabled="abnLookup.isLoading" />
                
                <p v-if="abnLookup.isLoading" class="form-text text-info mb-0 mt-1" style="font-size: 12px;"><i class="fas fa-spinner fa-spin me-1"></i>Validating {{ businessNumberShortLabel }}...</p>
                <p v-if="!abnLookup.isLoading && abnLookup.message" class="form-text mb-0 mt-1" style="font-size: 12px;"
                    :class="abnLookup.status === 'success' ? 'text-success' : 'text-danger'">
                    <i :class="abnLookup.status === 'success' ? 'fas fa-check-circle me-1' : 'fas fa-times-circle me-1'"></i>{{ abnLookup.message }}
                </p>
            </div>

            <div class="mb-3 text-start">
                <label for="leadOrganisationName" class="form-label" style="font-size: 13px;">Organisation Name <span class="text-danger">*</span> :</label>
                <select v-if="abnRecordFound && organisationNameOptions.length" id="leadOrganisationName" class="form-select" v-model="leadOrganisationName">
                    <option v-for="name in organisationNameOptions" :key="name" :value="name">{{ name }}</option>
                </select>
                <input v-else type="text" id="leadOrganisationName" v-model.trim="leadOrganisationName" class="form-control"
                    placeholder="Organisation Name" />
            </div>
        </div>
        <div class="mb-3">
            <label for="sentTo" class="form-label">To<span class="text-danger">*</span> :</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in sentToRecipients" :key="index" class="email-pill">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button type="button" class="email-pill-remove" @click="removeRecipient(index, 'sentTo')">×</button>
                </div>
                <input type="text" class="email-input-field" v-model="currentSentToInput"
                    @keydown.enter.prevent="addRecipient('sentTo')" @keydown.tab.prevent="addRecipient('sentTo')"
                    @blur="addRecipient('sentTo')" @paste="handlePaste('sentTo', $event)" placeholder="Add recipient">
            </div>
        </div>

        <div class="mb-3">
            <label for="cc" class="form-label">CC:</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in ccRecipients" :key="index" class="email-pill">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button type="button" class="email-pill-remove" @click="removeRecipient(index, 'cc')">×</button>
                </div>
                <input type="text" class="email-input-field" v-model="currentCcInput"
                    @keydown.enter.prevent="addRecipient('cc')" @keydown.tab.prevent="addRecipient('cc')"
                    @blur="addRecipient('cc')" @paste="handlePaste('cc', $event)" placeholder="Add CC recipient">
            </div>
        </div>

        <!-- <div class="mb-3">
            <label for="bcc" class="form-label">BCC:</label>
            <div class="email-input-container">
                <div v-for="(recipient, index) in bccRecipients" :key="index" class="email-pill">
                    <span>{{ recipient.displayName || recipient.email }}</span>
                    <button type="button" class="email-pill-remove" @click="removeRecipient(index, 'bcc')">×</button>
                </div>
                <input type="text" class="email-input-field" v-model="currentBccInput"
                    @keydown.enter.prevent="addRecipient('bcc')" @keydown.tab.prevent="addRecipient('bcc')"
                    @blur="addRecipient('bcc')" @paste="handlePaste('bcc', $event)" placeholder="Add BCC recipient">
            </div>
        </div> -->

        <div class="mb-3">
            <label for="customMessage" class="form-label">Custom Message:</label>
            <textarea disabled class="form-control" id="customMessage" v-model="customMessage" rows="4" maxlength="1000"
                placeholder="Enter an optional message..."></textarea>
            <div class="form-text text-end">{{ customMessage.length }} / 1000</div>
        </div>
        <div class="pt-3 pb-3 justify-content-center d-flex">
            <button type="button" class="btn btn-info text-light" @click="sendProposal()"
                :disabled="disableFormButton() || disableSendingButton">
                <i class="fas fa-paper-plane me-2"></i> Send
            </button>
        </div>
    </form>
</template>

<script>
import { log, toTitleCase, isValidEmail, cleanEmailString, extractEmails, parseEmailRecipient } from '@/plugins/helper.js'
import LoadingPage from '@/components/Utilities/vertex-loading.vue'
import {
    isAustralianCountry,
    getBusinessNumberLabel,
    getBusinessNumberShortLabel,
    getBusinessNumberPlaceholder,
    validateBusinessNumber,
} from '@/plugins/businessValidator.js'
export default {

    components: {
        LoadingPage
    },

    props: {
        initialSendTo: { // Prop for initial 'To' recipient
            type: String,
            default: ''
        },
        proposalID: { // Prop for Proposal ID
            type: String,
            default: ''
        },
        hasEditor: {
            type: Boolean,
            default: false
        },
        initialProposalTitle: {
            type: String,
            default: ''
        }
    },
    computed: {
        isAustralianLead() {
            return isAustralianCountry(this.leadCountryCode);
        },
        businessNumberLabel() {
            return getBusinessNumberLabel(this.leadCountryCode);
        },
        businessNumberShortLabel() {
            return getBusinessNumberShortLabel(this.leadCountryCode);
        },
        businessNumberPlaceholder() {
            return getBusinessNumberPlaceholder(this.leadCountryCode);
        },
    },
    data() {
        return {
            currentDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
            sentToRecipients: [],
            ccRecipients: [],
            bccRecipients: [],
            currentSentToInput: '',
            currentCcInput: '',
            currentBccInput: '',
            customMessage: '', // Data property for the custom message
            disableSendingButton: false,
            proposalTitle: this.initialProposalTitle || '',
            
            // Charity / ABN details variables
            leadIsCharity: false,
            leadCountryCode: '',
            leadAbn: '',
            leadOrganisationName: '',
            originalOrganisationName: '',
            lastOrgNameInTitle: '',
            initialLeadHasAbn: false,
            isLoadingProposalDetails: false,
            abnDetails: null,
            abnRecordFound: false,
            abnLookup: { isLoading: false, status: 'idle', message: '' },
            abnDebounceTimer: null,
            organisationNameOptions: [],
        };
    },

    emits: ["proposalSentSuccess"],

    methods: {
        isEmailValid(email) {
            return isValidEmail(email);
        },

        parseEmailInput(input) {
            const parsed = parseEmailRecipient(input);
            if (parsed && this.isEmailValid(parsed.email)) {
                return parsed;
            }
            return null;
        },

        addRecipient(field) {
            let inputField;
            let recipientArray;

            if (field === 'sentTo') {
                inputField = this.currentSentToInput;
                recipientArray = this.sentToRecipients;
            } else if (field === 'cc') {
                inputField = this.currentCcInput;
                recipientArray = this.ccRecipients;
            } else if (field === 'bcc') {
                inputField = this.currentBccInput;
                recipientArray = this.bccRecipients;
            }

            if (!inputField || !inputField.trim()) return;

            const extracted = extractEmails(inputField);
            if (extracted.length > 0) {
                extracted.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email === parsed.email);
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            } else {
                const rawInputs = inputField.split(/[,;]/).map(s => s.trim()).filter(Boolean);
                rawInputs.forEach(rawInput => {
                    const parsed = this.parseEmailInput(rawInput);
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = recipientArray.some(r => r.email === parsed.email);
                        if (!exists) {
                            recipientArray.push(parsed);
                        }
                    }
                });
            }

            if (field === 'sentTo') this.currentSentToInput = '';
            else if (field === 'cc') this.currentCcInput = '';
            else if (field === 'bcc') this.currentBccInput = '';
        },

        removeRecipient(index, field) {
            if (field === 'sentTo') {
                this.sentToRecipients.splice(index, 1);
            } else if (field === 'cc') {
                this.ccRecipients.splice(index, 1);
            } else if (field === 'bcc') {
                this.bccRecipients.splice(index, 1);
            }
        },

        handlePaste(field, event) {
            event.preventDefault();
            const pastedText = (event.clipboardData || window.clipboardData)?.getData('text') || '';
            const emails = extractEmails(pastedText);

            let targetRecipients;

            if (field === 'sentTo') {
                targetRecipients = this.sentToRecipients;
            } else if (field === 'cc') {
                targetRecipients = this.ccRecipients;
            } else if (field === 'bcc') {
                targetRecipients = this.bccRecipients;
            }

            if (emails.length > 0) {
                emails.forEach(email => {
                    const parsed = this.parseEmailInput(email) || { displayName: email, email };
                    if (parsed && this.isEmailValid(parsed.email)) {
                        const exists = targetRecipients.some(r => r.email === parsed.email);
                        if (!exists) {
                            targetRecipients.push(parsed);
                        }
                    }
                });
                if (field === 'sentTo') this.currentSentToInput = '';
                else if (field === 'cc') this.currentCcInput = '';
                else if (field === 'bcc') this.currentBccInput = '';
            } else {
                const cleaned = cleanEmailString(pastedText);
                if (field === 'sentTo') this.currentSentToInput = cleaned || pastedText;
                else if (field === 'cc') this.currentCcInput = cleaned || pastedText;
                else if (field === 'bcc') this.currentBccInput = cleaned || pastedText;
            }
        },

        async sendProposal() {

            if (this.hasEditor) {
                if (this.$parent.$refs.editorRef.isEditorContentDirty() || this.$parent.$refs.proposalDetailsFormRef.isShowDiscountChanged() == false) {
                    this.$toast.error("There are unsaved changes.");
                    return
                }
            }
            this.disableSendingButton = true

            let sentToEmails = this.sentToRecipients.map(recipient => recipient.email);
            let ccEmails = this.ccRecipients.map(recipient => recipient.email);
            let bccEmails = this.bccRecipients.map(recipient => recipient.email);

            let forSending = {
                sent_to: sentToEmails,
                cc: ccEmails,
                bcc: bccEmails,
                custom_message: this.customMessage, // Include custom message in payload
                proposal_title: this.proposalTitle ? this.proposalTitle.replace(/<[^>]*>?/gm, '').replace(/[\r\n\t]/g, ' ').trim() : this.proposalTitle,
                abn: this.isAustralianLead && !this.initialLeadHasAbn ? this.leadAbn : undefined,
                organisation_name: this.isAustralianLead && !this.initialLeadHasAbn ? this.leadOrganisationName : undefined
            }

            log("Proposal ID:", this.proposalID)
            log("For Sending:", forSending)

            await this.$axios.post("/proposals/" + this.proposalID + "/send", forSending)
                .then((response) => {
                    if (response.data.success) {
                        this.$toast.success(response.data.message)
                        this.$parent.proposal_client_email = ''
                        this.$emit("proposalSentSuccess")
                        this.resetForm();
                        this.disableSendingButton = false
                    } else {
                        this.$toast.error(response.data.message)
                        this.disableSendingButton = false
                    }
                })
                .catch((error) => {
                    this.$toast.error(error.response.data.message)
                    this.disableSendingButton = false
                })
        },

        resetForm() {
            this.sentToRecipients = [];
            this.ccRecipients = [];
            this.bccRecipients = [];
            this.currentSentToInput = '';
            this.currentCcInput = '';
            this.currentBccInput = '';
            this.customMessage = ''; // Reset the custom message
        },

        disableFormButton() {
            if (this.isLoadingProposalDetails) return true;
            if (this.sentToRecipients.length === 0) return true;
            if (this.isAustralianLead && !this.initialLeadHasAbn) {
                if (!this.leadAbn || !validateBusinessNumber(this.leadAbn, this.leadCountryCode)) return true;
                if (!this.leadOrganisationName) return true;
                if (this.abnLookup.status !== 'success') return true;
            }
            return false;
        },

        updateInitialSendTo(propValue) {
            this.sentToRecipients = [];

            if (propValue) {
                const parsedRecipient = this.parseEmailInput(propValue);
                if (parsedRecipient && this.isEmailValid(parsedRecipient.email)) {
                    this.sentToRecipients.push(parsedRecipient);
                    console.log('Initial recipient updated:', parsedRecipient);
                } else {
                    console.warn('Initial `initialSendTo` prop is not a valid email format:', propValue);
                }
            }
        },

        updateProposalTitleWithOrgName(newOrgName) {
            if (!newOrgName || !newOrgName.trim()) return;

            const newOrgProper = toTitleCase(newOrgName.trim());

            if (!this.proposalTitle) {
                this.proposalTitle = newOrgProper;
                this.lastOrgNameInTitle = newOrgProper;
                return;
            }

            const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            // 1. Try replacing lastOrgNameInTitle if present in proposalTitle
            if (this.lastOrgNameInTitle && this.lastOrgNameInTitle.trim()) {
                const regexLast = new RegExp(escapeRegExp(this.lastOrgNameInTitle.trim()), 'i');
                if (regexLast.test(this.proposalTitle)) {
                    this.proposalTitle = this.proposalTitle.replace(regexLast, newOrgProper);
                    this.lastOrgNameInTitle = newOrgProper;
                    return;
                }
            }

            // 2. Try replacing originalOrganisationName if present in proposalTitle
            if (this.originalOrganisationName && this.originalOrganisationName.trim()) {
                const regexOrig = new RegExp(escapeRegExp(this.originalOrganisationName.trim()), 'i');
                if (regexOrig.test(this.proposalTitle)) {
                    this.proposalTitle = this.proposalTitle.replace(regexOrig, newOrgProper);
                    this.lastOrgNameInTitle = newOrgProper;
                    return;
                }
            }

            // 3. If proposalTitle contains ' x ' (case-insensitive), replace the portion after ' x '
            const xMatch = this.proposalTitle.match(/(^.*?\bx\s+)(.*)$/i);
            if (xMatch) {
                this.proposalTitle = xMatch[1] + newOrgProper;
                this.lastOrgNameInTitle = newOrgProper;
                return;
            }
        },

        async fetchProposalDetails() {
            if (!this.proposalID) return;
            this.isLoadingProposalDetails = true;
            
            // Reset state
            this.leadIsCharity = false;
            this.leadCountryCode = '';
            this.leadAbn = '';
            this.leadOrganisationName = '';
            this.originalOrganisationName = '';
            this.lastOrgNameInTitle = '';
            this.initialLeadHasAbn = false;
            this.abnDetails = null;
            this.abnRecordFound = false;
            this.abnLookup = { isLoading: false, status: 'idle', message: '' };
            this.organisationNameOptions = [];

            try {
                const response = await this.$axios.get("/proposals/" + this.proposalID);
                const proposal = response.data.vertex_proposal;
                if (proposal) {
                    if (proposal.proposal_title && !this.initialProposalTitle) {
                        this.proposalTitle = proposal.proposal_title;
                    }
                    if (proposal.Lead) {
                        this.leadIsCharity = proposal.Lead.is_charity;
                        this.leadCountryCode = proposal.Lead.country_code || (proposal.Lead.organisation && proposal.Lead.organisation.country_code) || '';
                        const existingBusinessNumber = proposal.Lead.business_number || proposal.Lead.abn || (proposal.Lead.organisation && (proposal.Lead.organisation.business_number || proposal.Lead.organisation.abn)) || '';
                        this.leadAbn = existingBusinessNumber;
                        this.leadOrganisationName = proposal.Lead.organisation_name || '';
                        this.originalOrganisationName = proposal.Lead.organisation_name || '';
                        this.lastOrgNameInTitle = proposal.Lead.organisation_name || '';
                        this.initialLeadHasAbn = !!existingBusinessNumber;
                        if (this.leadAbn) {
                            this.abnLookup.status = 'success';
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch proposal details inside SendForm:", error);
            } finally {
                this.isLoadingProposalDetails = false;
            }
        },

        handleAbnInput() {
            clearTimeout(this.abnDebounceTimer);
            if (!this.leadAbn || this.leadAbn.trim() === '') {
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.organisationNameOptions = [];
                return;
            }
            this.abnDebounceTimer = setTimeout(() => { this.lookupABN(); }, 1000);
        },

        async lookupABN() {
            if (!this.leadAbn || this.leadAbn.trim() === '') {
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.abnLookup = { isLoading: false, status: 'idle', message: '' };
                this.organisationNameOptions = [];
                return;
            }

            if (!this.isAustralianLead) {
                const isValid = validateBusinessNumber(this.leadAbn, this.leadCountryCode);
                if (isValid) {
                    this.abnLookup = { isLoading: false, status: 'success', message: `${this.businessNumberShortLabel} format valid.` };
                } else {
                    this.abnLookup = { isLoading: false, status: 'error', message: `Invalid ${this.businessNumberShortLabel} format.` };
                }
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.organisationNameOptions = [];
                return;
            }

            const abn = this.leadAbn.replace(/\s/g, '');
            if (!abn || (abn.length !== 11 && abn.length !== 9)) {
                this.abnLookup = { isLoading: false, status: 'error', message: abn.length > 0 ? 'ABN must be 11 digits (or 9 digits for ACN).' : '' };
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.organisationNameOptions = [];
                return;
            }
            this.abnLookup.isLoading = true;
            this.abnLookup.message = '';
            this.abnLookup.status = 'idle';
            try {
                const resp = await this.$axios.post('/abn/look-up', {
                    abn: abn,
                });
                const details = resp.data.abn_details;
                this.abnDetails = details;
                this.abnRecordFound = true;

                if (this.leadIsCharity && !details.is_charity) {
                    this.abnLookup.status = 'error';
                    this.abnLookup.message = 'ABN is found but not registered as a charity.';
                    this.organisationNameOptions = [];
                } else {
                    this.abnLookup.status = 'success';
                    this.abnLookup.message = 'ABN Validated successfully.';
                    
                    // Build organisation name options
                    const names = [details.entity_name];
                    if (details.business_names && details.business_names.length > 0) {
                        const businessNames = details.business_names.map(bn => bn.name).filter(Boolean);
                        names.push(...businessNames);
                    }
                    this.organisationNameOptions = [...new Set(names)];
                    this.leadOrganisationName = details.entity_name;
                }
            } catch (error) {
                this.abnRecordFound = false;
                this.abnDetails = null;
                this.abnLookup.status = 'error';
                this.abnLookup.message = error.response?.data?.message || 'Invalid ABN';
                this.organisationNameOptions = [];
            } finally {
                this.abnLookup.isLoading = false;
            }
        },
    },

    watch: {
        initialSendTo: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.updateInitialSendTo(newVal);
                }
            },
            immediate: true
        },
        initialProposalTitle(newVal) {
            this.proposalTitle = newVal;
        },
        leadOrganisationName(newVal) {
            if (newVal) {
                this.updateProposalTitleWithOrgName(newVal);
            }
        },
        proposalID: {
            handler(newVal) {
                if (newVal) {
                    this.fetchProposalDetails();
                }
            },
            immediate: true
        }
    },

    mounted() {
        if (this.initialSendTo) {
            const parsedRecipient = this.parseEmailInput(this.initialSendTo);
            if (parsedRecipient && this.isEmailValid(parsedRecipient.email)) {
                this.sentToRecipients.push(parsedRecipient);
            } else {
                console.warn('Initial `initialSendTo` prop is not a valid email format:', this.initialSendTo);
            }
        }
    },

    created() {
        this.currentDate = new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    }
};
</script>

<style>
/* Container for the pills and input field */
.email-input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    padding: 0.375rem 0.75rem;
    min-height: 38px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.email-input-container:focus-within {
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Styling for each individual email pill */
.email-pill {
    display: inline-flex;
    align-items: center;
    background-color: #00a0c1;
    border: 1px solid #0087a3;
    border-radius: 16px;
    padding: 0.2rem 0.5rem;
    margin-right: 0.5rem;
    margin-bottom: 0.2rem;
    font-size: 0.9em;
    color: #ffffff;
    white-space: nowrap;
}

.email-pill-remove {
    background: none;
    border: none;
    color: #e0e0e0;
    font-size: 1.1em;
    line-height: 1;
    margin-left: 0.4rem;
    cursor: pointer;
    padding: 0 0.2rem;
    border-radius: 50%;
    transition: background-color 0.2s, color 0.2s;
}

.email-pill-remove:hover {
    background-color: #007c97;
    color: #ffffff;
}

/* Styling for the actual input field within the container */
.email-input-field {
    flex-grow: 1;
    border: none;
    outline: none;
    padding: 0;
    min-width: 80px;
    margin: 0;
    font-size: 1em;
    background: transparent;
}

.email-input-field:focus {
    outline: none;
    box-shadow: none;
}
</style>